/**
 * ImagesGrid Component
 *
 * Container with header (select all, count, bulk actions) and grid of ImageCards.
 * Includes empty state with visual tips.
 */

import { Image, Trash2, Download, Save, Loader2 } from 'lucide-react';
import { SectionHeader } from './SectionHeader';
import { ImageCard } from './ImageCard';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type { IImagesGridProps } from '@/@types/avatars';

const VISUAL_TIPS = [
  { emoji: '📸', label: 'Portrait shot' },
  { emoji: '🏢', label: 'Office setting' },
  { emoji: '🌳', label: 'Outdoor scene' },
];

export const ImagesGrid = ({
  images,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onDeselectAll,
  onView,
  onDownload,
  onEdit,
  onDelete,
  onBulkDelete,
  onBulkDownload,
  onSave,
  isGenerating = false,
}: IImagesGridProps) => {
  const hasImages = images.length > 0;
  const selectedCount = selectedIds.size;
  const allSelected = hasImages && selectedCount === images.length;
  const hasSelection = selectedCount > 0;

  const handleSelectAllClick = () => {
    if (allSelected) {
      onDeselectAll();
    } else {
      onSelectAll();
    }
  };

  return (
    <div className="flex-1 flex flex-col rounded-2xl border border-border bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-border">
        <div className="flex items-center gap-3">
          <SectionHeader step={3} label="Generated Images" />

          {hasImages && (
            <>
              <div
                role="button"
                tabIndex={0}
                className="flex items-center gap-2 cursor-pointer select-none hover:bg-muted/50 px-3 py-1.5 rounded-md transition-colors whitespace-nowrap border border-border"
                onClick={handleSelectAllClick}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelectAllClick();
                  }
                }}
              >
                <Checkbox
                  checked={allSelected}
                  className="border-muted-foreground data-[state=checked]:bg-primary data-[state=checked]:border-primary pointer-events-none"
                />
                <span className="text-sm text-muted-foreground">
                  {allSelected ? 'Deselect all' : 'Select all'}
                </span>
              </div>

              {hasSelection && (
                <span className="px-2 py-1 text-xs rounded-full bg-primary/10 text-primary font-medium whitespace-nowrap">
                  {selectedCount} selected
                </span>
              )}
            </>
          )}
        </div>

        {/* Bulk Actions */}
        <div className="flex items-center gap-2">
          {hasSelection && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={onBulkDelete}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </Button>

              <Button variant="ghost" size="sm" onClick={onBulkDownload}>
                <Download className="w-4 h-4" />
                <span>Download</span>
              </Button>
            </>
          )}

          <Button
            variant="secondary"
            size="sm"
            onClick={onSave}
            disabled={!hasSelection}
            className={cn('bg-secondary text-secondary-foreground', !hasSelection && 'opacity-50')}
          >
            <Save className="w-4 h-4" />
            <span>Save as Avatar</span>
          </Button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        {isGenerating ? (
          <GeneratingState />
        ) : hasImages ? (
          <div
            className={cn(
              'grid gap-4 h-full',
              // Single image: center with max width
              images.length === 1 && 'grid-cols-1 max-w-[200px] mx-auto',
              // 2 images: 2 columns
              images.length === 2 && 'grid-cols-2 max-w-lg mx-auto',
              // 3 images: 3 columns
              images.length === 3 && 'grid-cols-3 max-w-2xl mx-auto',
              // 4+ images: 4 columns
              images.length >= 4 && 'grid-cols-4 max-w-3xl mx-auto'
            )}
          >
            {images.map((image, _index) => (
              <div key={image.storageId} className="">
                <ImageCard
                  image={image}
                  isSelected={selectedIds.has(image.storageId)}
                  onToggleSelect={() => onToggleSelect(image.storageId)}
                  onView={() => onView(image)}
                  onDownload={() => onDownload(image)}
                  onEdit={() => onEdit(image)}
                  onDelete={() => onDelete(image.storageId)}
                  aspectRatio={image.aspectRatio}
                />
              </div>
            ))}
          </div>
        ) : (
          <EmptyState />
        )}
      </div>
    </div>
  );
};

const EmptyState = () => {
  return (
    <div className="flex flex-col items-center justify-center h-full py-12">
      {/* Icon */}
      <div className="w-[100px] h-[100px] rounded-full bg-background-input flex items-center justify-center mb-6">
        <Image className="w-10 h-10 text-muted-foreground opacity-60" />
      </div>

      {/* Text */}
      <h3 className="text-base font-medium text-muted-foreground mb-2">No images generated yet</h3>
      <p className="text-sm text-muted-foreground text-center max-w-[320px] mb-6">
        Write a prompt above and click Generate to create your AI avatar
      </p>

      {/* Visual Tips */}
      <div className="flex items-center gap-3">
        {VISUAL_TIPS.map(tip => (
          <div
            key={tip.label}
            className="flex items-center gap-2 px-3 py-2 rounded-full bg-background-input border border-border"
          >
            <span className="text-sm">{tip.emoji}</span>
            <span className="text-xs text-muted-foreground">{tip.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const GeneratingState = () => {
  return (
    <div className="flex flex-col items-center justify-center h-full py-12">
      <Loader2 className="w-12 h-12 text-primary animate-spin mb-6" />
      <h3 className="text-base font-medium text-foreground mb-2">Generating images...</h3>
      <p className="text-sm text-muted-foreground">This may take a moment</p>
    </div>
  );
};
